import React from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { PilotLoader } from '@/shared/components/feedback/PilotLoader';
import { useCaptainWorkspaceConfig } from '@/features/captain/queries/useCaptainWorkspaceConfig';
import { RootErrorBoundary } from './root-error-boundary';
import { AuthGuard } from './AuthGuard';

const LoginPage = React.lazy(async () => {
  const module = await import('@/pages/login/LoginPage');
  return { default: module.LoginPage };
});

const ForgotPasswordPage = React.lazy(async () => {
  const module = await import('@/pages/forgot-password/ForgotPasswordPage');
  return { default: module.ForgotPasswordPage };
});

const ResetPasswordPage = React.lazy(async () => {
  const module = await import('@/pages/reset-password/ResetPasswordPage');
  return { default: module.ResetPasswordPage };
});

const SetupPage = React.lazy(async () => {
  const module = await import('@/pages/setup/SetupPage');
  return { default: module.SetupPage };
});

const HomePage = React.lazy(async () => {
  const module = await import('@/pages/home/HomePage');
  return { default: module.HomePage };
});

const ConversationsPage = React.lazy(async () => {
  const module = await import('@/pages/conversations/ConversationsPage');
  return { default: module.ConversationsPage };
});

const ContactsPage = React.lazy(async () => {
  const module = await import('@/pages/contacts/ContactsPage');
  return { default: module.ContactsPage };
});

const ContactDetailPage = React.lazy(async () => {
  const module = await import('@/pages/contacts/ContactDetailPage');
  return { default: module.ContactDetailPage };
});

const WorkspacesPage = React.lazy(async () => {
  const module = await import('@/pages/workspaces/WorkspacesPage');
  return { default: module.WorkspacesPage };
});

const OpportunitiesPage = React.lazy(async () => {
  const module = await import('@/pages/opportunities/OpportunitiesPage');
  return { default: module.OpportunitiesPage };
});

const RoutingPage = React.lazy(async () => {
  const module = await import('@/pages/routing/RoutingPage');
  return { default: module.RoutingPage };
});

const SettingsPage = React.lazy(async () => {
  const module = await import('@/pages/settings/SettingsPage');
  return { default: module.SettingsPage };
});

const CampaignsPage = React.lazy(async () => {
  const module = await import('@/pages/campaigns/CampaignsPage');
  return { default: module.CampaignsPage };
});

const CrmCampaignBuilderPage = React.lazy(async () => {
  const module = await import('@/pages/campaigns/CrmCampaignBuilderPage');
  return { default: module.CrmCampaignBuilderPage };
});

const AgentsPage = React.lazy(async () => {
  const module = await import('@/pages/agents/AgentsPage');
  return { default: module.AgentsPage };
});

const AgentConfigPage = React.lazy(async () => {
  const module = await import('@/pages/agents/AgentConfigPage');
  return { default: module.AgentConfigPage };
});

const PublicCaptainPage = React.lazy(async () => {
  const module = await import('@/pages/public/PublicCaptainPage');
  return { default: module.PublicCaptainPage };
});

const PublicCaptainSessionsPage = React.lazy(async () => {
  const module = await import('@/pages/admin/PublicCaptainSessionsPage');
  return { default: module.PublicCaptainSessionsPage };
});

const PublicCaptainSessionDetailPage = React.lazy(async () => {
  const module = await import('@/pages/admin/PublicCaptainSessionDetailPage');
  return { default: module.PublicCaptainSessionDetailPage };
});

function RouteSuspenseFallback(): React.ReactNode {
  const { t } = useTranslation();
  return <PilotLoader message={t('corepilot.contacts.filterMeta.loading')} />;
}

function withRouteSuspense(element: React.ReactElement): React.ReactElement {
  return <React.Suspense fallback={<RouteSuspenseFallback />}>{element}</React.Suspense>;
}

/* Role-aware landing for `/`. Default destination is the Captain home;
 * we positively redirect *only* the front-line roles (sales rep, sales
 * manager) to /conversations, since that's their primary working surface.
 * Everyone else — admins, managers in other functions, and unresolved /
 * fallback states — stays on Captain so a flaky /captain/config/me
 * response can't accidentally bounce an admin away from their home page.
 * The auth-layer `useIsSuperAdmin` flag (Coretava staff) is intentionally
 * NOT consulted here: it's orthogonal to the user's effective workplace
 * role, so a staff member signed-in as sales_manager should still get
 * the sales_manager landing. While the config is in flight we render
 * the suspense fallback to avoid a closed-then-open flicker. */
function RoleAwareHome(): React.ReactElement {
  const { role, isLoading, hasResolvedData } = useCaptainWorkspaceConfig();
  if (isLoading) return <RouteSuspenseFallback />;
  if (hasResolvedData && (role === 'sales_rep' || role === 'sales_manager')) {
    return <Navigate to="/conversations" replace />;
  }
  return <HomePage />;
}

export const router = createBrowserRouter([
  {
    errorElement: <RootErrorBoundary />,
    children: [
      { path: '/c', element: withRouteSuspense(<PublicCaptainPage />) },
      { path: '/login', element: withRouteSuspense(<LoginPage />) },
      { path: '/forgot-password', element: withRouteSuspense(<ForgotPasswordPage />) },
      { path: '/reset-password', element: withRouteSuspense(<ResetPasswordPage />) },
      { path: '/setup', element: withRouteSuspense(<SetupPage />) },
      {
        path: '/workspaces',
        element: (
          <AuthGuard requireActiveApp={false}>
            {withRouteSuspense(<WorkspacesPage />)}
          </AuthGuard>
        ),
      },
      {
        path: '/',
        element: (
          <AuthGuard>
            {withRouteSuspense(<RoleAwareHome />)}
          </AuthGuard>
        ),
      },
      {
        /* Single conversations route. Selection is driven by query-string params:
         *   - `?contactId=:contactId` — deep-link from contact-details / WhatsApp CTA. The
         *     page resolves the contact's most recent conversation through the backend
         *     filter and lands on it.
         *   - `?conversationId=:conversationId` — used for orphan rows that haven't been
         *     linked to a CRM contact yet, so the URL still uniquely identifies the open
         *     conversation across refresh / share / back-button. */
        path: '/conversations',
        element: (
          <AuthGuard>
            {withRouteSuspense(<ConversationsPage />)}
          </AuthGuard>
        ),
      },
      {
        path: '/contacts',
        element: (
          <AuthGuard>
            {withRouteSuspense(<ContactsPage />)}
          </AuthGuard>
        ),
      },
      {
        path: '/contacts/:contactId',
        element: (
          <AuthGuard>
            {withRouteSuspense(<ContactDetailPage />)}
          </AuthGuard>
        ),
      },
      {
        path: '/campaigns',
        element: (
          <AuthGuard>
            {withRouteSuspense(<CampaignsPage />)}
          </AuthGuard>
        ),
      },
      {
        path: '/campaigns/builder/:adCampaignId',
        element: (
          <AuthGuard>
            {withRouteSuspense(<CrmCampaignBuilderPage />)}
          </AuthGuard>
        ),
      },
      {
        path: '/opportunities',
        element: (
          <AuthGuard>
            {withRouteSuspense(<OpportunitiesPage />)}
          </AuthGuard>
        ),
      },
      {
        path: '/routing',
        element: (
          <AuthGuard>
            {withRouteSuspense(<RoutingPage />)}
          </AuthGuard>
        ),
      },
      {
        path: '/agents',
        element: (
          <AuthGuard>
            {withRouteSuspense(<AgentsPage />)}
          </AuthGuard>
        ),
      },
      {
        path: '/agents/:agentId',
        element: (
          <AuthGuard>
            {withRouteSuspense(<AgentConfigPage />)}
          </AuthGuard>
        ),
      },
      {
        path: '/settings',
        element: (
          <AuthGuard>
            {withRouteSuspense(<SettingsPage />)}
          </AuthGuard>
        ),
      },
      {
        path: '/admin/captain-sessions',
        element: (
          <AuthGuard>
            {withRouteSuspense(<PublicCaptainSessionsPage />)}
          </AuthGuard>
        ),
      },
      {
        path: '/admin/captain-sessions/:token',
        element: (
          <AuthGuard>
            {withRouteSuspense(<PublicCaptainSessionDetailPage />)}
          </AuthGuard>
        ),
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
